import type { BaseEntityState, AuditFields, ServerListParams } from '@crm/shared';
import type { TaskRecurrentUnit } from './taskRecurrentUnits';

// ─── Enum union types ──────────────────────────────────────────────────
// I valori sono allineati ai label canonici degli enum Java in
// `modules/crm-util/src/main/java/it/dvel/crm/util/constants/Task*.java`.
// Il backend REST serializza int32 (`getValue()`); la conversione
// string↔int vive in `taskMappers.ts` ed e' applicata ai boundary dentro
// `taskApi.ts`.
//
// SYNC CROSS-LAYER — modifiche vanno replicate in parallelo su:
//   TaskType:         modules/crm-util/.../TaskType.java,         taskMappers.ts (TASK_TYPE_TO_INT),
//                     TASK_TYPES array sotto, locales/{it,en}.json (task.types.*)
//   TaskPriority:     modules/crm-util/.../TaskPriority.java,     taskMappers.ts (TASK_PRIORITY_TO_INT),
//                     TASK_PRIORITIES array sotto, locales/{it,en}.json (task.priorities.*)
//   TaskStatus:       modules/crm-util/.../TaskStatus.java,       taskMappers.ts (TASK_STATUS_TO_INT),
//                     locales/{it,en}.json (task.statuses.*)
//   TaskRecurrentUnit: modules/crm-util/.../TaskRecurrentUnit.java, taskRecurrentUnits.ts (union +
//                     TASK_RECURRENT_UNITS), taskMappers.ts (TASK_RECURRENT_UNIT_TO_INT),
//                     locales/{it,en}.json (task.recurrent.units.*)
export type TaskType =
  | 'activity'
  | 'call'
  | 'whatsapp_call'
  | 'linkedin_call'
  | 'email'
  | 'meeting'
  | 'note';
export type TaskPriority = 'none' | 'low' | 'medium' | 'high';
export type TaskStatus = 'todo' | 'imminent' | 'expiring_today' | 'expired' | 'completed';
export type TaskTab = TaskStatus | 'all';

// SYNC: i `value` ricalcano l'union TaskType sopra; i `label` sono chiavi
// i18n. Aggiunte/rinominazioni vanno replicate in TaskType (sopra),
// taskMappers.ts (TASK_TYPE_TO_INT), Java TaskType.java, locales JSON.
export const TASK_TYPES: { value: TaskType; label: string }[] = [
  { value: 'call', label: 'task.types.calls' },
  { value: 'whatsapp_call', label: 'task.types.whatsapp' },
  { value: 'linkedin_call', label: 'task.types.linkedin' },
  { value: 'email', label: 'task.types.email' },
  { value: 'meeting', label: 'task.types.meetings' },
  { value: 'activity', label: 'task.types.operations' },
  { value: 'note', label: 'task.types.notes' },
];

/**
 * Mappa ActivityItem.type (UI filter value) -> TaskType (backend label).
 * Usata dai bottoni "+ Nuova X" nelle tab tipologia di ActivityTimeline
 * (Person/Business/QuotationDetail) per decidere quale tipo di task
 * creare. I tipi 'sistema' e 'file' non sono creabili manualmente.
 */
export const ACTIVITY_TO_TASK_TYPE: Partial<Record<string, TaskType>> = {
  nota: 'note',
  email: 'email',
  chiamata: 'call',
  riunione: 'meeting',
  // LinkedinPost ha entità dedicata (non crea un Task): la voce serve solo a
  // far passare il guard `if (!taskType) return null` di getCreateAction, che poi
  // intercetta `type === 'linkedin'` per aprire LinkedinPostFormModal.
  linkedin: 'linkedin_call',
  task: 'activity',
};

// SYNC: vedi TaskPriority sopra + taskMappers.ts (TASK_PRIORITY_TO_INT) +
// TaskPriority.java + locales/{it,en}.json (task.priorities.*).
export const TASK_PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: 'none', label: 'task.priorities.none' },
  { value: 'high', label: 'task.priorities.high' },
  { value: 'medium', label: 'task.priorities.medium' },
  { value: 'low', label: 'task.priorities.low' },
];

/**
 * Scorciatoie della data di scadenza, espresse come giorni da OGGI. `days: null` è la voce
 * "personalizzata", che apre un selettore di data invece di calcolare.
 *
 * Vive qui e non in `TaskForm` per due ragioni. La prima è tecnica: esportare una costante da un
 * file di componente rompe l'HMR (`react-refresh/only-export-components`), come già accaduto con
 * `STATUS_CHIP_COLOR`. La seconda è che i consumatori sono due — il form del Task e la
 * configurazione dell'azione di automazione "crea attività" — e i giorni scritti due volte sarebbero
 * due elenchi da tenere allineati a mano.
 *
 * L'automazione usa le sole voci con `days` valorizzato: una data fissa scelta in configurazione
 * sarebbe già passata al secondo giro del motore, quindi lì "personalizzata" non ha senso.
 */
export type DeadlinePreset = 'today' | 'tomorrow' | 'threeDays' | 'oneWeek' | 'custom';

export const DEADLINE_PRESETS: { value: DeadlinePreset; label: string; days: number | null }[] = [
  { value: 'today', label: 'task.deadlinePresets.today', days: 0 },
  { value: 'tomorrow', label: 'task.deadlinePresets.tomorrow', days: 1 },
  { value: 'threeDays', label: 'task.deadlinePresets.threeDays', days: 3 },
  { value: 'oneWeek', label: 'task.deadlinePresets.oneWeek', days: 7 },
  { value: 'custom', label: 'task.deadlinePresets.custom', days: null },
];

export type ReminderPreset =
  | 'none'
  | 'atExpiration'
  | 'min30'
  | 'hour1'
  | 'day1'
  | 'week1'
  | 'custom';

/**
 * Preset del promemoria, espressi come offset in MINUTI prima della scadenza. `offsetMinutes` a
 * `null` copre i due casi che un offset non ce l'hanno: `none` (nessun promemoria) e `custom`
 * (data e ora scelte a mano). `atExpiration` è invece offset zero, cioè un valore vero.
 *
 * Vive qui per la stessa ragione di {@link DEADLINE_PRESETS}: i consumatori sono il form del Task e
 * le impostazioni predefinite dell'account, e due elenchi erano già divergenti — quello delle
 * impostazioni ne offriva cinque, senza `week1` e senza `custom`.
 *
 * Chi ne offre un sottoinsieme lo dichiara per valori (vedi `AccountSettings`) e non filtrando su
 * `offsetMinutes`: `none` e `custom` hanno entrambi `null` pur essendo casi opposti, quindi nessuna
 * proprietà di questo elenco separa le voci come servirebbe.
 */
export const REMINDER_PRESETS: {
  value: ReminderPreset;
  label: string;
  offsetMinutes: number | null;
}[] = [
  { value: 'none', label: 'task.reminderPresets.none', offsetMinutes: null },
  { value: 'atExpiration', label: 'task.reminderPresets.atExpiration', offsetMinutes: 0 },
  { value: 'min30', label: 'task.reminderPresets.min30', offsetMinutes: 30 },
  { value: 'hour1', label: 'task.reminderPresets.hour1', offsetMinutes: 60 },
  { value: 'day1', label: 'task.reminderPresets.day1', offsetMinutes: 1440 },
  { value: 'week1', label: 'task.reminderPresets.week1', offsetMinutes: 10080 },
  { value: 'custom', label: 'task.reminderPresets.custom', offsetMinutes: null },
];

export interface Task extends AuditFields {
  taskId: number;
  title: string;
  type: TaskType;
  priority: TaskPriority;
  expirationDate: string | null;
  remindDate: string | null;
  recurrent: boolean;
  recurrentValue: number;
  recurrentUnit: TaskRecurrentUnit;
  assignedUserId: number;
  queueId: number | null;
  notes: string | null;
  status: TaskStatus;
  /**
   * Stato del funnel in cui il task e' collocato, cioe' la colonna della bacheca. `0` = non
   * collocato.
   *
   * ASSE INDIPENDENTE da `status`: quello dice se il task e' da fare o fatto, questo in che colonna
   * sta. Un task chiuso puo' stare in una colonna qualsiasi, e un task da fare pure.
   *
   * Non e' un campo del task lato database: lo dice una riga di `PipelineMembership`, e il server
   * lo risolve prima di rispondere.
   */
  currentStateId: number;
}

/** Parametri di lista del task: quelli comuni piu' la bacheca. */
export interface TaskListParams extends ServerListParams {
  /**
   * Se valorizzato, restituisce i task della bacheca indicata, cioe' quelli collocati in uno degli
   * stati della sua pipeline. Ha precedenza sugli altri filtri.
   */
  kanbanId?: number;
  /** Solo i task con scadenza da qui in poi (ISO); quelli senza scadenza restano fuori (FILO-41). */
  expirationDateFrom?: string;
  /** Solo i task con scadenza prima di qui (ISO, escluso). */
  expirationDateTo?: string;
}

export interface TaskState extends BaseEntityState<Task> {
  activeTab: TaskTab;
  filters: {
    assignedUserId?: number;
    type?: TaskType;
    queueId?: number;
  };
  /** Totale records lato server per pagination DataGrid. */
  totalCount: number;
}

export interface TaskCreatePayload {
  title: string;
  type: TaskType;
  priority: TaskPriority;
  expirationDate: string | null;
  remindDate: string | null;
  recurrent: boolean;
  recurrentValue: number;
  recurrentUnit: TaskRecurrentUnit;
  assignedUserId: number;
  queueId: number | null;
  notes: string | null;
  /**
   * Colonna del funnel in cui far nascere il task. Opzionale: un task senza
   * collocazione e' normale, al contrario del preventivo per cui lo stato e' obbligatorio.
   */
  currentStateId?: number;
}

export interface TaskUpdatePayload extends Partial<TaskCreatePayload> {
  taskId: number;
  /** Stato del funnel in cui collocare il task. Canale dedicato lato server, come `status`. */
  currentStateId?: number;
}
